<template>
    <div class="dashboard-item br-m sm-b-s"
        :class="[
            childSizeClasses,
            {
                'dashboard-item--grid': gridLayout,
                [`dashboard-item--span-${currentRowSpan}`]: gridLayout && currentRowSpan > 1,
                [`dashboard-item--cols-${currentColSpan}`]: gridLayout && hasCustomColSpan
            }
        ]"
        :draggable="hoveringDragButton && editMode"
        @drop.prevent="onDrop"
        @dragstart="onDragStart"
        @dragenter="onDragEnter"
        @dragover.prevent
        @dragleave.prevent
        @mouseover="hovering = true"
        @mouseleave="hovering = false">
        <div class="f drag-drop-line" v-if="isDragHovered">
            <div class="br-m line-color top-dot"></div>
            <div class="line-color fg center-line"></div>
            <div class="br-m line-color bottom-dot"></div>
        </div>
        <div v-if="showEditActions" class="f drag-buttons z1">
            <div v-if="gridLayout" class="drag-buttons-section f bf br-m sp-xs sm-r-s te-fat-light fa-c ws-nw">
                {{ currentColSpan }} : {{ currentRowSpan }}
            </div>
            <div class="drag-buttons-section f bf br-m sp-xs">
                <FakeButtonIcon class="el--s-m c-el-panel-bg bf"
                    icon="move"
                    @mouseover.native="hoveringDragButton = true"
                    @mouseleave.native="hoveringDragButton = false" />
                <ButtonIcon v-if="canCustomize"
                    class="el--s-m sm-l-xs"
                    icon="pencil5"
                    @click="onCustomizeItem" />
                <template v-if="gridLayout">
                    <ButtonIcon class="el--s-m sm-l-xs"
                        icon="chevrons-expand-vertical"
                        title="mead.increase_widget_height"
                        :disabled="atMaxRowSpan"
                        @click="changeRowSpan(1)" />
                    <ButtonIcon class="el--s-m sm-l-xs"
                        icon="chevrons-contract-vertical"
                        title="mead.decrease_widget_height"
                        :disabled="atMinRowSpan"
                        @click="changeRowSpan(-1)" />
                    <ButtonIcon class="el--s-m sm-l-xs"
                        icon="chevrons-expand-horizontal"
                        title="mead.increase_widget_width"
                        :disabled="atMaxColSpan"
                        @click="changeColSpan(1)" />
                    <ButtonIcon class="el--s-m sm-l-xs"
                        icon="chevrons-contract-horizontal"
                        title="mead.decrease_widget_width"
                        :disabled="atMinColSpan"
                        @click="changeColSpan(-1)" />
                </template>
                <ButtonIcon class="el--s-m sm-l-xs"
                    icon="copy"
                    :disabled="atChildLimit"
                    @click="copyItem" />
                <ButtonIcon class="el--s-m sm-l-xs"
                    icon="cross"
                    @click="openDeleteModal" />
            </div>
        </div>
        <slot name="child"></slot>
        <AddEditModal v-if="showAddEditModal"
            :childType="type"
            :childProps="clonedChildProps"
            :dashboardType="dashboardType"
            :dashboard="dashboard"
            :requestEditMode="true"
            @confirm="onEditChildWidget"
            @close="onCloseAddEditModal" />
        <DeleteModal v-if="showDeleteModal"
            type="widget"
            warningText="mead.delete_widget_warning"
            @confirm="onRemoveItem"
            @close="onCloseDeleteModal" />
    </div>
</template>
<script>
import { guuid, isNeObject } from '@aretheyhappy/utils';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import FakeButtonIcon from '../../atoms/FakeButtonIcon.vue';
import AddEditModal from './modals/DashboardAddEditModal.vue';
import DeleteModal from './modals/DashboardDeleteWidgetModal.vue';

// Tallest a widget can be in the grid layout, in grid rows
const MAX_ROW_SPAN = 4;
// Number of columns in the grid layout
const GRID_COLUMNS = 12;
// Window width where the lg size classes start, matches $mq-dm in sass/_variables.scss
const LG_BREAKPOINT = 1280;

export default {
    props: {
        /**
         * Unique identifier for this dashboard item/widget. Used to track drag-and-drop
         * state and to identify the widget in the copy, remove, and edit events it emits.
         */
        uid: {type: String, default: ''},
        /**
         * Unique identifier of the parent panel/row that contains this item. Used to
         * scope drag-and-drop interactions to items within the same panel.
         */
        parentUid: {type: String, default: ''},
        /**
         * Component type for this child item. Used to look up the widget's
         * definition for sizing, customization, and rendering.
         */
        type: {type: String, default: ''},
        /**
         * Type of dashboard we are working with. Passed through to the
         * add/edit modal for context when customizing this item.
         */
        dashboardType: {type: String, default: ''},
        /**
         * If we are in edit mode or not. Controls whether the drag handle
         * and edit actions (customize, copy, remove) are shown.
         */
        editMode: {type: Boolean, default: false},
        /**
         * Uid of the child item currently being dragged, used to distinguish
         * child-level drag-and-drop events from panel-level ones (an empty
         * value means no child is currently being dragged).
         */
        draggedChildUid: {type: String, default: ''},
        /**
         * Uid of the child item currently hovered during drag-and-drop, used
         * to determine if this item is the current drop target.
         */
        hoveredChildUid: {type: String, default: ''},
        /**
         * Uid of the parent panel containing the child item being dragged,
         * used to scope drag-and-drop events to items within the same panel.
         */
        draggedChildPanelUid: {type: String, default: ''},
        /**
         * Properties sent into the child component. Cloned via computed
         * before being handed to the edit modal so edits don't mutate this
         * widget's props by reference.
         */
        childProps: {type: Object, default: () => {}},
        /**
         * If the row is at its limit. Disables the copy action when true.
         */
        atChildLimit: {type: Boolean, default: false},
        /**
         * Entire Dashboard object. Passed through to the add/edit modal for context.
         */
        dashboard: {type: Object, default: () => ({})},
        /**
         * If the parent row is using the CSS grid layout. Enables custom widths
         * and heights, and the increase/decrease width and height buttons in edit mode.
         */
        gridLayout: {type: Boolean, default: false},
        /**
         * Number of grid rows this item spans (from `options.row_span`), from
         * 1 up to 4. Only used when `gridLayout` is true.
         */
        rowSpan: {type: Number, default: 1},
        /**
         * Number of grid columns (out of 12) this item spans (from `options.col_span`).
         * When not set, the width comes from the widget's size classes. Only used when
         * `gridLayout` is true, and only from the md breakpoint up.
         */
        colSpan: {type: Number, default: undefined},
    },
    components: {FakeButtonIcon, AddEditModal, DeleteModal, ButtonIcon},
    data: function () {
        return {
            hoveringDragButton: false,
            hovering: false,
            showAddEditModal: false,
            showDeleteModal: false,
            // Tracked so the default width follows the active size class breakpoint
            windowWidth: window.innerWidth,
            resizeListenerUid: guuid(),
        };
    },
    methods: {
        /**
         * Triggered when drop event happens on hovered child
         */
        onDrop: function () {
            // Ignore panel drag events
            if (this.draggedChildUid.length === 0 || this.parentUid !== this.draggedChildPanelUid) return;
            this.$emit('onDrop');
        },
        /**
         * User initiated drag from the drag handle
         */
        onDragStart: function () {
            // If we are not hovering the drag button, dont allow drag and drop
            if (!this.hoveringDragButton) return;
            this.$emit('onDragStart', this.uid);
        },
        /**
         * When we are in drag/drop mode and this child is entered
         */
        onDragEnter: function () {
            // Ignore panel drag events. And drag events from other panels.
            if (this.draggedChildUid.length === 0 || this.parentUid !== this.draggedChildPanelUid) return;
            this.$emit('onDragEnter', this.uid);
        },
        /**
         * When remove button is clicked on the UI
         */
        openDeleteModal: function () {
            this.showDeleteModal = true;
        },
        /**
         * Close the delete modal
         */
        onCloseDeleteModal: function () {
            this.showDeleteModal = false;
        },
        /**
         * When delete is confirmed in the delete modal
         */
        onRemoveItem: function () {
            this.$emit('onRemoveItem', this.uid);
        },
        /**
         * Copy widget in the same row
         */
        copyItem: function () {
            this.$emit('onCopyItem', this.uid);
        },
        /**
         * Show customization widget
         */
        onCustomizeItem: function () {
            this.showAddEditModal = true;
        },
        /**
         * Called when widget changes are confirmed
         * @param options
         */
        onEditChildWidget: function (options) {
            this.showAddEditModal = false;
            const newOptions = Object.assign({}, options.options);
            // Ensure the consumer cant delete the grid span information 
            for (const key of ['row_span', 'col_span']) {
                if (newOptions[key] === undefined && this.childProps && this.childProps[key] !== undefined) {
                    newOptions[key] = this.childProps[key];
                }
            }
            this.$emit('onUpdateItem', this.uid, newOptions)
        },
        /**
         * Grow or shrink the widget height in grid layout, kept between 1 and MAX_ROW_SPAN
         * @param change - number of rows to add (negative to remove)
         */
        changeRowSpan: function (change) {
            const span = Math.min(Math.max(this.currentRowSpan + change, 1), MAX_ROW_SPAN);
            if (span === this.currentRowSpan) return;
            this.$emit('onUpdateItem', this.uid, Object.assign({}, this.clonedChildProps, {
                row_span: span,
            }));
        },
        /**
         * Grow or shrink the widget width in grid layout, kept between 1 and GRID_COLUMNS
         * @param change - number of columns to add (negative to remove)
         */
        changeColSpan: function (change) {
            const span = Math.min(Math.max(this.currentColSpan + change, 1), GRID_COLUMNS);
            if (span === this.currentColSpan) return;
            this.$emit('onUpdateItem', this.uid, Object.assign({}, this.clonedChildProps, {
                col_span: span,
            }));
        },
        /**
         * Close the editing modal
         */
        onCloseAddEditModal: function () {
            this.showAddEditModal = false;
        }
    },
    computed: {
        /**
         * Number of grid rows this item spans, clamped to 1 - MAX_ROW_SPAN
         */
        currentRowSpan: function () {
            const span = parseInt(this.rowSpan, 10);
            if (isNaN(span)) return 1;
            return Math.min(Math.max(span, 1), MAX_ROW_SPAN);
        },
        /**
         * If this item is already at the tallest allowed height
         */
        atMaxRowSpan: function () {
            return this.currentRowSpan >= MAX_ROW_SPAN;
        },
        /**
         * If this item is already at the shortest allowed height
         */
        atMinRowSpan: function () {
            return this.currentRowSpan <= 1;
        },
        /**
         * If a valid custom width has been set on this item
         */
        hasCustomColSpan: function () {
            return !isNaN(parseInt(this.colSpan, 10));
        },
        /**
         * Starting width in grid columns, taken from the widget's size classes for the
         * active breakpoint. Custom widths only apply from md up, so this checks lg (on
         * large screens) then md, falling back to sm and xs.
         */
        defaultColSpan: function () {
            const sizes = {};
            const matcher = /(?:^|\s)col-(xs|sm|md|lg)-(?:min-)?(\d+)(?=\s|$)/g;
            let match;
            while ((match = matcher.exec(this.childSizeClasses || '')) !== null) {
                sizes[match[1]] = parseInt(match[2], 10);
            }
            const breakpoints = this.windowWidth >= LG_BREAKPOINT ? ['lg', 'md', 'sm', 'xs'] : ['md', 'sm', 'xs'];
            for (const bp of breakpoints) {
                if (sizes[bp] !== undefined) return sizes[bp];
            }
            return GRID_COLUMNS;
        },
        /**
         * Number of grid columns this item spans, either the custom width or the
         * size class default, clamped to 1 - GRID_COLUMNS
         */
        currentColSpan: function () {
            const span = this.hasCustomColSpan ? parseInt(this.colSpan, 10) : this.defaultColSpan;
            return Math.min(Math.max(span, 1), GRID_COLUMNS);
        },
        /**
         * If this item is already the full width of the row
         */
        atMaxColSpan: function () {
            return this.currentColSpan >= GRID_COLUMNS;
        },
        /**
         * If this item is already at the narrowest allowed width
         */
        atMinColSpan: function () {
            return this.currentColSpan <= 1;
        },
        /**
         * If this child is currently the target of drag and drop
         */
        isDragHovered: function () {
            return this.hoveredChildUid === this.uid && this.parentUid === this.draggedChildPanelUid;
        },
        /**
         * Get and size classes used for this widget
         */
        childSizeClasses: function () {
            const widget = MeadCommon.DashboardService.getWidgetDefinition(this.type);
            if (widget !== undefined) {
                return widget.getSizeClasses();
            }
            return '';
        },
        /**
         * If edit actions should be shown.
         * Only show them in edit mode while hovering a panel
         */
        showEditActions: function () {
            return this.editMode && this.hovering;
        },
        /**
         * If the current widget type can be customized via the edit modal.
         */
        canCustomize: function () {
            const widget = MeadCommon.DashboardService.getWidgetDefinition(this.type);
            if (widget !== undefined) {
                return widget.hasEditConfiguration;
            }
            return false;
        },
        /**
         * Clone child props to remove the reference so they dont update
         * this widget when editing.
         */
        clonedChildProps: function () {
            if (isNeObject(this.childProps))
                return JSON.parse(JSON.stringify(this.childProps));
            return {};
        }
    },
    created: function () {
        MeadCommon.GlobalListeners.bind();
        MeadCommon.Flash.subscribe(this.resizeListenerUid, MeadCommon.GLOBAL_EVENT_TYPES.WINDOW_RESIZE, () => {
            this.windowWidth = window.innerWidth;
        });
    },
    destroyed: function () {
        MeadCommon.Flash.unsubscribe(this.resizeListenerUid);
    },
}
</script>
<style lang="scss" scoped>
@import '../../../../sass/variables';
.line-color {
    background-color: #333333;
}

:root[ath-theme="dark"] {
    .line-color {
        background-color: #FFFFFF;
    }
}

html[printmode] {
    .dashboard-item {
        break-inside: avoid;
    }
}

@media print {
    .dashboard-item {
        break-inside: avoid;
    }
}

.dashboard-item {
    position: relative;

    // This removes the parent background color when doing drag and drop
    &[draggable="true"] {
        position: relative;
        z-index: 1;
    }

    .drag-drop-line {
        height: 100%;
        position: absolute;
        left: -.4rem;

        .top-dot {
            width: 1rem;
            height: 1rem;
            position: absolute;
            top: -0.2rem;
        }

        .center-line {
            height: 100%;
            margin-top: 0.25rem;
            position: absolute;
            width: 0.5rem;
            left: .2rem;
        }

        .bottom-dot {
            width: 1rem;
            height: 1rem;
            position: absolute;
            bottom: -1rem;
        }
    }

    .drag-buttons {
        position: absolute;
        top: -1rem;
        right: -.1rem;

        .drag-buttons-section {
            background: var(--c-panel-bg);
        }
    }
}
</style>